import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Star, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import httpClient from '@/services/httpClient';
import './PublicFeedbackForm.css';

interface Question {
  id: number;
  type: 'short_text' | 'paragraph' | 'multiple_choice_single' | 'multiple_choice_multi' | 'star_rating';
  text: string;
  required: boolean;
  options?: string[];
}

interface FormData {
  id: number;
  name: string;
  description: string;
  collectName: boolean;
  collectEmail: boolean;
  questions: Question[];
}

interface Answer {
  questionId: number;
  text?: string;
  options?: string[];
  rating?: number;
}

type FormStatus = 'loading' | 'ready' | 'submitting' | 'success' | 'error' | 'closed';

const PublicFeedbackForm: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [formData, setFormData] = useState<FormData | null>(null);
  const [status, setStatus] = useState<FormStatus>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  const [respondentName, setRespondentName] = useState('');
  const [respondentEmail, setRespondentEmail] = useState('');
  const [answers, setAnswers] = useState<Record<number, Answer>>({});

  useEffect(() => {
    fetchForm();
  }, [slug]);

  const fetchForm = async () => {
    try {
      setStatus('loading');
      const response = await httpClient.get<{ success: boolean; data: any; error?: string }>(
        `/public/feedback/${slug}`
      );

      if (response.success && response.data) {
        const form = response.data;
        setFormData({
          id: form.id,
          name: form.name,
          description: form.description || '',
          collectName: form.collect_name === 1,
          collectEmail: form.collect_email === 1,
          questions: (form.questions || []).map((q: any) => ({
            id: q.id,
            type: q.question_type,
            text: q.question_text,
            required: q.is_required === 1,
            options: q.options ? (typeof q.options === 'string' ? JSON.parse(q.options) : q.options) : undefined
          }))
        });
        setStatus('ready');
      } else {
        setErrorMessage(response.error || 'Form not found');
        setStatus('error');
      }
    } catch (error: any) {
      if (error?.response?.status === 410) {
        setErrorMessage('This form is no longer accepting responses.');
        setStatus('closed');
      } else {
        setErrorMessage(error?.message || 'Failed to load form');
        setStatus('error');
      }
    }
  };

  const generateBrowserFingerprint = (): string => {
    const nav = window.navigator;
    const screen = window.screen;
    const fingerprint = [
      nav.userAgent,
      nav.language,
      screen.colorDepth,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      nav.cookieEnabled ? '1' : '0',
      nav.hardwareConcurrency || 0
    ].join('|');

    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(16);
  };

  const handleAnswerChange = (questionId: number, value: string | string[] | number, type: Question['type']) => {
    setAnswers(prev => {
      const newAnswer: Answer = { questionId };

      if (type === 'star_rating') {
        newAnswer.rating = value as number;
      } else if (type === 'multiple_choice_multi') {
        newAnswer.options = value as string[];
      } else if (type === 'multiple_choice_single') {
        newAnswer.options = [value as string];
      } else {
        newAnswer.text = value as string;
      }

      return { ...prev, [questionId]: newAnswer };
    });
  };

  const handleMultiOptionToggle = (questionId: number, option: string) => {
    setAnswers(prev => {
      const currentOptions = prev[questionId]?.options || [];
      const newOptions = currentOptions.includes(option)
        ? currentOptions.filter(o => o !== option)
        : [...currentOptions, option];

      return {
        ...prev,
        [questionId]: { questionId, options: newOptions }
      };
    });
  };

  const validateForm = (): boolean => {
    if (!formData) return false;

    for (const question of formData.questions) {
      if (question.required) {
        const answer = answers[question.id];
        if (!answer) return false;

        if (question.type === 'star_rating' && !answer.rating) return false;
        if ((question.type === 'multiple_choice_single' || question.type === 'multiple_choice_multi')
            && (!answer.options || answer.options.length === 0)) return false;
        if ((question.type === 'short_text' || question.type === 'paragraph')
            && (!answer.text || answer.text.trim() === '')) return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      setErrorMessage('Please answer all required questions.');
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      const response = await httpClient.post(`/public/feedback/${slug}/submit`, {
        browserFingerprint: generateBrowserFingerprint(),
        respondentName: respondentName || null,
        respondentEmail: respondentEmail || null,
        answers: Object.values(answers).map(a => ({
          questionId: a.questionId,
          text: a.text || null,
          options: a.options || null,
          rating: a.rating || null
        }))
      });

      if (response.success) {
        setStatus('success');
      } else {
        setErrorMessage(response.error || 'Failed to submit response');
        setStatus('ready');
      }
    } catch (error: any) {
      if (error?.response?.status === 409) {
        setErrorMessage('You have already submitted a response to this form.');
      } else if (error?.response?.status === 410) {
        setErrorMessage('This form is no longer accepting responses.');
        setStatus('closed');
      } else {
        setErrorMessage(error?.message || 'Failed to submit response');
      }
      setStatus('ready');
    }
  };

  const renderQuestion = (question: Question, index: number) => {
    const answer = answers[question.id];

    return (
      <div key={question.id} className="public-feedback-question">
        <div className="public-feedback-question-header">
          <span className="public-feedback-question-number">{index + 1}</span>
          <label className="public-feedback-question-text">
            {question.text}
            {question.required && <span className="public-feedback-required">*</span>}
          </label>
        </div>

        <div className="public-feedback-question-input">
          {question.type === 'short_text' && (
            <input
              type="text"
              className="public-feedback-input"
              placeholder="Your answer..."
              value={answer?.text || ''}
              onChange={(e) => handleAnswerChange(question.id, e.target.value, question.type)}
            />
          )}

          {question.type === 'paragraph' && (
            <textarea
              className="public-feedback-textarea"
              placeholder="Your answer..."
              rows={4}
              value={answer?.text || ''}
              onChange={(e) => handleAnswerChange(question.id, e.target.value, question.type)}
            />
          )}

          {question.type === 'multiple_choice_single' && question.options && (
            <div className="public-feedback-options">
              {question.options.map((option, i) => (
                <label key={i} className="public-feedback-radio-option">
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    checked={answer?.options?.[0] === option}
                    onChange={() => handleAnswerChange(question.id, option, question.type)}
                  />
                  <span className="public-feedback-radio-custom" />
                  <span className="public-feedback-option-text">{option}</span>
                </label>
              ))}
            </div>
          )}

          {question.type === 'multiple_choice_multi' && question.options && (
            <div className="public-feedback-options">
              {question.options.map((option, i) => (
                <label key={i} className="public-feedback-checkbox-option">
                  <input
                    type="checkbox"
                    checked={answer?.options?.includes(option) || false}
                    onChange={() => handleMultiOptionToggle(question.id, option)}
                  />
                  <span className="public-feedback-checkbox-custom" />
                  <span className="public-feedback-option-text">{option}</span>
                </label>
              ))}
            </div>
          )}

          {question.type === 'star_rating' && (
            <div className="public-feedback-stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className={`public-feedback-star ${(answer?.rating || 0) >= star ? 'active' : ''}`}
                  onClick={() => handleAnswerChange(question.id, star, question.type)}
                >
                  <Star size={32} fill={(answer?.rating || 0) >= star ? '#F97316' : 'none'} />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (status === 'loading') {
    return (
      <div className="public-feedback-container">
        <div className="public-feedback-loading">
          <Loader2 size={48} className="public-feedback-spinner" />
          <p>Loading form...</p>
        </div>
      </div>
    );
  }

  if (status === 'error' || status === 'closed') {
    return (
      <div className="public-feedback-container">
        <div className="public-feedback-error">
          <AlertCircle size={48} />
          <h2>{status === 'closed' ? 'Form Closed' : 'Form Not Available'}</h2>
          <p>{errorMessage}</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="public-feedback-container">
        <div className="public-feedback-success">
          <CheckCircle size={64} />
          <h2>Thank You!</h2>
          <p>Your response has been submitted successfully.</p>
        </div>
      </div>
    );
  }

  if (!formData) return null;

  return (
    <div className="public-feedback-container">
      <div className="public-feedback-card">
        <div className="public-feedback-header">
          <h1>{formData.name}</h1>
          {formData.description && <p>{formData.description}</p>}
        </div>

        <form onSubmit={handleSubmit} className="public-feedback-form">
          {(formData.collectName || formData.collectEmail) && (
            <div className="public-feedback-respondent-info">
              {formData.collectName && (
                <div className="public-feedback-field">
                  <label>Your Name (optional)</label>
                  <input
                    type="text"
                    className="public-feedback-input"
                    placeholder="Enter your name"
                    value={respondentName}
                    onChange={(e) => setRespondentName(e.target.value)}
                  />
                </div>
              )}
              {formData.collectEmail && (
                <div className="public-feedback-field">
                  <label>Your Email (optional)</label>
                  <input
                    type="email"
                    className="public-feedback-input"
                    placeholder="Enter your email"
                    value={respondentEmail}
                    onChange={(e) => setRespondentEmail(e.target.value)}
                  />
                </div>
              )}
            </div>
          )}

          <div className="public-feedback-questions">
            {formData.questions.map((question, index) => renderQuestion(question, index))}
          </div>

          {errorMessage && (
            <div className="public-feedback-error-message">
              <AlertCircle size={16} />
              <span>{errorMessage}</span>
            </div>
          )}

          <button
            type="submit"
            className="public-feedback-submit"
            disabled={status === 'submitting'}
          >
            {status === 'submitting' ? (
              <>
                <Loader2 size={20} className="public-feedback-spinner" />
                Submitting...
              </>
            ) : (
              'Submit Response'
            )}
          </button>
        </form>

        <div className="public-feedback-footer">
          <p>Powered by Magnetix</p>
        </div>
      </div>
    </div>
  );
};

export default PublicFeedbackForm;
